import React from 'react';
import { Box, Stack, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AccessTime, CameraAlt, Person, Store, Info } from '@mui/icons-material';
import { format } from 'date-fns';
import PhotoCard from './PhotoCard';

/**
 * Renders a single activity stage (check-in / break / check-out) with its
 * timestamp and optional selfie / store photos.
 *
 * Props:
 *   stageKey – e.g. 'checkin'
 *   label    – display label
 *   color    – accent colour for the stage
 *   data     – the stage object from the activity row (or 'NA' / null)
 */
export default function ActivityStageBlock({ stageKey, label, color, data }) {
  const isActive  = data && data !== 'NA';
  const time      = isActive && data.timeReported ? format(new Date(data.timeReported), 'hh:mm a') : null;
  const hasSelfie = isActive && data.imageSelfie?.data;
  const hasStore  = isActive && data.imageStore?.data;
  const hasPhotos = hasSelfie || hasStore;

  return (
    <Box sx={{
      borderRadius: '14px',
      border: `1.5px solid ${isActive ? alpha(color, 0.25) : '#e8eef4'}`,
      background: isActive ? alpha(color, 0.03) : '#fafbfc',
      overflow: 'hidden',
      opacity: isActive ? 1 : 0.55,
      transition: 'all 0.2s',
    }}>
      {/* Stage header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.5,
        borderBottom: isActive && hasPhotos ? `1px solid ${alpha(color, 0.12)}` : 'none',
        background: isActive ? alpha(color, 0.06) : 'transparent',
      }}>
        {/* Status dot */}
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: isActive ? color : '#c8d4dc',
          boxShadow: isActive ? `0 0 8px ${alpha(color, 0.5)}` : 'none',
        }} />

        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: isActive ? color : '#9aa8b4', flex: 1 }}>
          {label}
        </Typography>

        {time && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 13, color: alpha(color, 0.7) }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: alpha(color, 0.9), fontFamily: 'monospace' }}>
              {time}
            </Typography>
          </Box>
        )}

        {!isActive && (
          <Typography sx={{ fontSize: '0.72rem', color: '#b0bec5', fontStyle: 'italic' }}>Not yet</Typography>
        )}

        {isActive && data.notes && (
          <Tooltip title={data.notes}>
            <Info sx={{ fontSize: 15, color: alpha(color, 0.6), cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>

      {/* Photos */}
      {isActive && hasPhotos && (
        <Box sx={{ p: 1.5, pt: 1.25 }}>
          <Stack direction="row" spacing={1.5}>
            {hasSelfie && (
              <PhotoCard src={data.imageSelfie.data} label="Selfie" icon={<Person sx={{ fontSize: 13 }} />} />
            )}
            {hasStore && (
              <PhotoCard src={data.imageStore.data} label="Store" icon={<Store sx={{ fontSize: 13 }} />} />
            )}
            {/* Filler to keep grid balanced */}
            {hasSelfie && !hasStore && <Box sx={{ flex: 1 }} />}
            {!hasSelfie && hasStore && <Box sx={{ flex: 1 }} />}
          </Stack>
        </Box>
      )}

      {isActive && !hasPhotos && (
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CameraAlt sx={{ fontSize: 14, color: '#b0bec5' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#b0bec5' }}>No photos</Typography>
        </Box>
      )}
    </Box>
  );
}
